import React, { Dispatch, FC, ReactElement, SetStateAction, SyntheticEvent, useState } from "react";
import { KeyedMutator } from "swr";
import { Application } from "../../models/application";
import {
  ActionModalConfirmation,
  ActionModalInfo,
  ApplicationWithCategoryType
} from "../../pages/applications/applications";
import CloseApplicationTool from "../action-tools/close-application-tool";
import CopyApplicationTool from "../action-tools/copy-application-tool";
import DeleteApplicationTool from "../action-tools/delete-application-tool";
import ShareApplicationTool from "../action-tools/share-application-tool";
import DropdownMenu, { AlignmentHorizontal } from "../dropdown-menu/dropdown-menu";

interface ApplicationActionsProps {
  application: ApplicationWithCategoryType;
  setActionModalInfo: Dispatch<SetStateAction<ActionModalInfo>>;
  setActionModalConfirmation: Dispatch<SetStateAction<ActionModalConfirmation>>;
  refetchApplications: KeyedMutator<Application[]>;
  isMobile?: boolean;
}

interface GetActionList {
  (application: ApplicationWithCategoryType): Action[];
}

interface GetActionTool {
  (action: Action, iconButton?: boolean, align?: AlignmentHorizontal): ReactElement<HTMLButtonElement>;
}

enum Action {
  Copy = "Copy",
  Share = "Share",
  Delete = "Delete",
  Close = "Close"
}

const ApplicationActions: FC<ApplicationActionsProps> = ({
  application,
  setActionModalInfo,
  setActionModalConfirmation,
  refetchApplications,
  isMobile
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [activeItemIndex, setActiveItemIndex] = useState<number | null>(null);

  const getActionList: GetActionList = (application) => {
    const actions = [
      application.isCopyable ? Action.Copy : null,
      application.isShareable ? Action.Share : null,
      application.isDeletable ? Action.Delete : null,
      (isMobile && application.isCopyable) || (isMobile && application.isShareable) ? Action.Close : null
    ].filter((action) => action !== null) as Action[];

    return actions;
  };

  const handleOnClose = (event: SyntheticEvent) => {
    event.preventDefault();
    setOpen(!open);
    if (activeItemIndex === null) {
      setActiveItemIndex(0);
    } else {
      setActiveItemIndex(null);
    }
  };

  const getActionTool: GetActionTool = (action, iconButton) => {
    switch (action) {
      case Action.Copy:
        return (
          <CopyApplicationTool
            application={application}
            key={action}
            iconButton={iconButton}
            refetchApplications={refetchApplications}
            setActionModalConfirmation={setActionModalConfirmation}
            setActionModalInfo={setActionModalInfo}
          />
        );
      case Action.Share:
        return <ShareApplicationTool application={application} key={action} iconButton={iconButton} />;
      case Action.Delete:
        return (
          <DeleteApplicationTool
            application={application}
            setActionModalConfirmation={setActionModalConfirmation}
            setActionModalInfo={setActionModalInfo}
            key={action}
            refetchApplications={refetchApplications}
            iconButton={iconButton}
          />
        );
      case Action.Close:
        return <CloseApplicationTool handleClose={handleOnClose} />;
    }
  };

  const actionList = getActionList(application);

  const actionTools: ReactElement<HTMLButtonElement>[] = actionList.map((action) => {
    return getActionTool(action);
  });

  const singleActionTool =
    actionList.length === 1
      ? actionList.map((action) => {
          return getActionTool(action, true);
        })
      : null;

  return (
    <>
      {actionTools.length > 0 ? (
        actionTools.length === 1 ? (
          singleActionTool
        ) : (
          <DropdownMenu
            listPosition="right"
            items={actionTools}
            isMobile={isMobile}
            align={AlignmentHorizontal.Left}
            isOpen={open}
          />
        )
      ) : null}
    </>
  );
};

export default ApplicationActions;
